import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { useMarket, priceLabel } from "@/market";

const TRADES = [
  { name: "Chauffagiste", line: "Chaudières, tubage, entretien, radiateurs", img: "/assets/metiers/chauffage.webp", slug: "chauffage" },
  { name: "Plombier / Sanitaire", line: "Fuites, sanitaires, eau chaude, tuyauteries", img: "/assets/metiers/plomberie.webp", slug: "plomberie" },
  { name: "Électricien", line: "Tableaux, mise en conformité, éclairage", img: "/assets/metiers/electricite.webp", slug: "electricite" },
  { name: "Climatisation / PAC", line: "Splits, pompes à chaleur, liaisons", img: "/assets/metiers/climatisation.webp", slug: "climatisation" },
  { name: "Ventilation", line: "Systèmes C et D, gaines, entretien", img: "/assets/metiers/ventilation.webp", slug: "ventilation" },
  { name: "Couvreur / Toiture", line: "Tuiles, zinguerie, étanchéité", img: "/assets/metiers/toiture.webp", slug: "toiture" },
  { name: "Photovoltaïque", line: "Panneaux, onduleurs, batteries", img: "/assets/metiers/photovoltaique.webp", slug: "photovoltaique" },
  { name: "Menuisier", line: "Châssis, portes, aménagements bois", img: "/assets/metiers/menuiserie.webp", slug: "menuiserie" },
  { name: "Maçon", line: "Murs, ouvertures, transformations", img: "/assets/metiers/maconnerie.webp", slug: "maconnerie" },
  { name: "Plafonneur / Plâtrier", line: "Enduits, cloisons, faux plafonds", img: "/assets/metiers/platrerie.webp", slug: "platrerie" },
  { name: "Carreleur", line: "Sols, murs, salles de bain", img: "/assets/metiers/carrelage.webp", slug: "carrelage" },
  { name: "Peintre", line: "Intérieur, extérieur, finitions", img: "/assets/metiers/peinture.webp", slug: "peinture" },
  { name: "Isolation", line: "Toitures, murs, combles, PEB", img: "/assets/metiers/isolation.webp", slug: "isolation" },
];

export const TradesForSection = () => {
  const { t } = useLanguage();
  const { market } = useMarket();

  return (
    <section id="metiers" data-testid="trades-for-section" className="py-24 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#3A5CC8]/25 bg-[#EEF1FC] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">
            {t("Pour qui ?")}
          </span>
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0F2645]">
            {t("Pensé pour tous les corps de métier du bâtiment")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#5C6A87] leading-relaxed">
            {t("Que vous soyez chauffagiste, électricien, couvreur ou carreleur, Devisto capture votre chantier sur place et prépare vos devis. Un seul tarif :")} {priceLabel(market)}/{t("mois par société, vos ouvriers inclus.")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {TRADES.map((trade, i) => (
            <motion.article
              key={trade.slug}
              data-testid={`trade-card-${trade.slug}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              className="group relative overflow-hidden rounded-2xl border border-[#E2E7F7] bg-slate-900 shadow-sm hover:shadow-xl transition-shadow duration-300"
            >
              <div className="aspect-[4/5] sm:aspect-[4/3] overflow-hidden">
                <img
                  src={trade.img}
                  alt={trade.name}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.06] transition-all duration-500"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F2645]/95 via-[#0F2645]/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-heading text-base sm:text-lg font-bold text-white leading-tight">{t(trade.name)}</h3>
                <p className="mt-1 text-[11px] sm:text-xs text-blue-100/80 leading-snug">{t(trade.line)}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link
            to="/studio/inscription"
            data-testid="trades-cta-studio"
            className="inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-7 py-4 text-sm font-semibold text-white hover:bg-[#3A5CC8] active:scale-[0.98] transition-all duration-200"
          >
            {t("Créer mon compte artisan")} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-sm text-[#5C6A87]">
            {t("Votre métier n'est pas dans la liste ? Devisto s'adapte à toutes les activités du bâtiment.")}
          </p>
        </div>
      </div>
    </section>
  );
};
